import { useMemo } from "react";
import { extractData } from "../../utils/analytics/extractData";
import {
  useGetSubscriptionRevenueQuery,
  useGetSubscriptionHomeGraphQuery,
} from "../../store/api/analyticsApi";
import { processDateRangeData } from "../../utils/analytics/processDateRangeData";
import { groupData } from "../../utils/analytics/groupData";

const EMPTY_CHART = {
  xAxis: [],
  yAxis: [],
  percentageChange: 0,
  netValue: 0,
  formattedPercentageChange: "0%",
};

export const useSubscriptionData = (
  dateRange,
  defaultChannel,
  usersData = {},
  isContentPartner = false,
  enabled = true
) => {
  const { activeSubscriptions } = usersData || {};

    const params = useMemo(() => {
      if (!defaultChannel || !dateRange?.[0]) return null;
      return extractData(dateRange, defaultChannel);
    }, [dateRange, defaultChannel]);

    const skip = !params || !enabled;

    const { data: revenueData, isFetching: revenueFetching } = useGetSubscriptionRevenueQuery(
      params ? { pubId: params.pub_id, startDate: params.prev_start_date, endDate: params.end_date } : {},
      { skip: skip || !isContentPartner }
    );

    const { data: subscriptionHomeGraph, isFetching: subscriptionHomeGraphFetching } = useGetSubscriptionHomeGraphQuery(
      params
        ? {
            channelId: params.channel_id,
            startDate: params.prev_start_date,
            endDate: params.end_date,
          }
        : {},
      { skip: skip || isContentPartner }
    );

    const totalRevenueData = useMemo(() => {
      if (!params) return EMPTY_CHART;

      if (!isContentPartner && subscriptionHomeGraph) {
        const raw = processDateRangeData(
          subscriptionHomeGraph,
          { responsePath: "subscription_revenue", xAxisKey: "day", extractValue: (entry) => parseFloat(entry.total) },
          params
        );
        return groupData(raw, dateRange);
      }

      if (!revenueData) return EMPTY_CHART;
      const raw = processDateRangeData(revenueData, { responsePath: "data", xAxisKey: isContentPartner ? "filter_timestamp" : "day", extractValue: (entry) => parseFloat(isContentPartner ? entry.revenue_share : entry.total) }, params);
      return groupData(raw, dateRange);
    }, [subscriptionHomeGraph, revenueData, params, dateRange, isContentPartner]);

    const cancellationsData = useMemo(() => {
      if (!params) return EMPTY_CHART;

      if (!isContentPartner && subscriptionHomeGraph) {
        const raw = processDateRangeData(
          subscriptionHomeGraph,
          { responsePath: "cancelled_subscriptions", xAxisKey: "day", extractValue: (entry) => parseFloat(entry.total) },
          params
        );
        return groupData(raw, dateRange);
      }

      return EMPTY_CHART;
    }, [subscriptionHomeGraph, params, dateRange, isContentPartner]);

    const newSubscribersData = useMemo(() => {
      if (!params) return EMPTY_CHART;

      if (!isContentPartner && subscriptionHomeGraph) {
        const raw = processDateRangeData(
          subscriptionHomeGraph,
          { responsePath: "new_subscribers", xAxisKey: "day", extractValue: (entry) => parseFloat(entry.total) },
          params
        );
        return groupData(raw, dateRange);
      }

      return EMPTY_CHART;
    }, [subscriptionHomeGraph, params, dateRange, isContentPartner]);

  return {
    loading: enabled
      ? (isContentPartner ? revenueFetching : subscriptionHomeGraphFetching)
      : false,
    activeSubscriptions,
    newSubscribersData,
    totalRevenueData,
    cancellationsData,
  };
};

export default useSubscriptionData;
